import React, { useState } from "react"; 
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Box,
  Typography,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  Divider,
} from "@mui/material";
import {
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  ExitToApp as LogoutIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useAuthContext } from "../../../context/AuthContext";
import { getGuestDisplayName, getGuestId } from "../../../utils/guestDisplayUtils";
import AuthModal from "./AuthModal";

const API_BASE = process.env.REACT_APP_API_URL;

const resolveProfileImage = (user) => {
  const pp = user?.profile_picture || '';
  if (!pp) return { imgSrc: undefined, isEmoji: false, emoji: null };
  const isEmoji = pp.startsWith('emoji:');
  if (isEmoji) return { imgSrc: undefined, isEmoji: true, emoji: pp.replace('emoji:', '') };
  if (pp.startsWith('http')) return { imgSrc: pp, isEmoji: false, emoji: null };
  return { imgSrc: `${API_BASE}${pp}`, isEmoji: false, emoji: null };
};

const UserAuthButton = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthContext();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [authMode, setAuthMode] = useState("login"); // 'login' or 'register'

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAuthModalOpen = (mode) => {
    setAuthMode(mode);
    setAuthModalOpen(true);
    handleMenuClose();
  };

  const handleAuthModalClose = () => {
    setAuthModalOpen(false);
  };

  const handleAuthSuccess = (user) => {
    // console.log('Authentication successful:', user);
    handleAuthModalClose();
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    // When login required is ON, redirect to /login after logout; on API failure assume ON (fail-safe)
    fetch(`${API_BASE}/api/settings/public/login-required`)
      .then((res) => res.json())
      .then((data) => {
        if (data.loginRequired) {
          navigate('/login', { replace: true });
        }
      })
      .catch(() => {
        navigate('/login', { replace: true });
      });
  };

  if (!isAuthenticated || !user) {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            gap: { xs: 0.8, sm: 1 },
            flexWrap: { xs: "nowrap", sm: "wrap" },
            justifyContent: { xs: "center", sm: "flex-start" },
            alignItems: "center",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<LoginIcon />}
            onClick={() => handleAuthModalOpen("login")}
            sx={{
              borderColor: "rgba(255, 0, 255, 0.5)",
              color: "#fff",
              textTransform: "none",
              borderRadius: "25px",
              px: { xs: 2, sm: 2.5 },
              py: { xs: 0.6, sm: 0.75 },
              fontSize: { xs: "0.8rem", sm: "0.95rem" },
              fontWeight: 600,
              minWidth: { xs: 120, sm: "auto" },
              border: "2px solid rgba(255, 0, 255, 0.5)",
              "&:hover": {
                borderColor: "#ff00ff",
                backgroundColor: "rgba(255, 0, 255, 0.15)",
                boxShadow: "0 0 20px rgba(255,0,255,0.4)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Sign In
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => handleAuthModalOpen("register")}
            sx={{
              background: "linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)",
              color: "#fff",
              textTransform: "none",
              borderRadius: "25px",
              px: { xs: 2, sm: 2.5 },
              py: { xs: 0.6, sm: 0.75 },
              fontSize: { xs: "0.8rem", sm: "0.95rem" },
              minWidth: { xs: 120, sm: "auto" },
              fontWeight: 700,
              border: "2px solid rgba(255,255,255,0.2)",
              boxShadow: "0 4px 20px rgba(255,0,255,0.4)",
              "&:hover": {
                background: "linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)",
                transform: "translateY(-2px)",
                boxShadow:
                  "0 6px 30px rgba(255,0,255,0.6), 0 0 20px rgba(0,255,255,0.4)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Sign Up
          </Button>
        </Box>

        <AuthModal
          open={authModalOpen}
          onClose={handleAuthModalClose}
          onSuccess={handleAuthSuccess}
          initialMode={authMode}
        />
      </>
    );
  }

  return (
    <>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 } }}
      >
        <Chip
          avatar={
            (() => {
              const { imgSrc, isEmoji, emoji } = resolveProfileImage(user);
              return (
                <Avatar
                  src={imgSrc}
                  imgProps={{ referrerPolicy: 'no-referrer' }}
                  sx={{
                    background: imgSrc ? "transparent" : "linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)",
                    width: { xs: 32, sm: 40 },
                    height: { xs: 32, sm: 40 },
                    border: imgSrc ? "3px solid rgba(255, 0, 255, 0.6)" : "2px solid rgba(255,255,255,0.3)",
                    boxShadow: "0 4px 20px rgba(255,0,255,0.5)",
                    fontSize: isEmoji ? '1.3rem' : undefined,
                  }}
                >
                  {isEmoji ? emoji : imgSrc ? null : user.user_type === "guest" ? (
                    <PersonIcon sx={{ color: "#fff" }} />
                  ) : (
                    <Typography sx={{ color: "#fff", fontWeight: 600 }}>
                      {user.fname?.charAt(0)?.toUpperCase() || "U"}
                    </Typography>
                  )}
                </Avatar>
              );
            })()
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#fff",
                  fontSize: { xs: "0.8rem", sm: "0.95rem" },
                  whiteSpace: "nowrap",
                  maxWidth: { xs: 90, sm: "none" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user.user_type === "guest"
                  ? getGuestDisplayName(user)
                  : user.fname}
              </Typography>
              {user.user_type === "guest" && (
                <Chip
                  label="Guest"
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: "rgba(255, 255, 255, 0.4)",
                    color: "rgba(255, 255, 255, 0.9)",
                    height: { xs: 18, sm: 22 },
                    fontSize: { xs: "0.6rem", sm: "0.7rem" },
                    fontWeight: 500,
                  }}
                />
              )}
            </Box>
          }
          onClick={handleMenuOpen}
          sx={{
            cursor: "pointer",
            background:
              "linear-gradient(135deg, rgba(255,0,255,0.2) 0%, rgba(0,255,255,0.2) 100%)",
            backdropFilter: "blur(10px)",
            border: "2px solid rgba(255, 0, 255, 0.4)",
            color: "#fff",
            height: { xs: 42, sm: 52 },
            px: { xs: 1, sm: 1.5 },
            boxShadow: "0 4px 20px rgba(255,0,255,0.3)",
            "&:hover": {
              background:
                "linear-gradient(135deg, rgba(255,0,255,0.3) 0%, rgba(0,255,255,0.3) 100%)",
              borderColor: "rgba(255, 0, 255, 0.6)",
              transform: "translateY(-2px)",
              boxShadow:
                "0 8px 30px rgba(255,0,255,0.5), 0 0 20px rgba(0,255,255,0.3)",
            },
            transition: "all 0.3s ease",
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            background:
              "linear-gradient(135deg, rgba(20,0,40,0.98) 0%, rgba(40,0,60,0.98) 100%)",
            backdropFilter: "blur(20px)",
            minWidth: { xs: 220, sm: 280 },
            maxWidth: { xs: 250, sm: 320 },
            mt: { xs: 1, sm: 1.5 },
            border: "2px solid rgba(255,0,255,0.3)",
            borderRadius: "16px",
            boxShadow:
              "0 10px 40px rgba(255,0,255,0.4), 0 0 20px rgba(0,255,255,0.2)",
            overflow: "visible",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 3 },
            py: { xs: 1.5, sm: 3 },
            background:
              "linear-gradient(135deg, rgba(255,0,255,0.2) 0%, rgba(0,255,255,0.2) 100%)",
            borderBottom: "1px solid rgba(255,0,255,0.3)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {(() => {
              const { imgSrc, isEmoji, emoji } = resolveProfileImage(user);
              return (
            <Avatar
              src={imgSrc}
              imgProps={{ referrerPolicy: 'no-referrer' }}
              sx={{
                background:
                  imgSrc
                    ? "transparent"
                    : "linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)",
                width: { xs: 40, sm: 72 },
                height: { xs: 40, sm: 72 },
                border:
                  imgSrc
                    ? "3px solid rgba(255, 0, 255, 0.6)"
                    : "2px solid rgba(255,255,255,0.3)",
                boxShadow: "0 8px 32px rgba(255,0,255,0.6)",
                fontSize: isEmoji ? { xs: '1.4rem', sm: '2rem' } : undefined,
              }}
            >
              {imgSrc ? null : isEmoji ? emoji : user.user_type === "guest" ? (
                <PersonIcon
                  sx={{ fontSize: { xs: 20, sm: 32 }, color: "#fff" }}
                />
              ) : (
                <Typography
                  sx={{ color: "#fff", fontWeight: 700, fontSize: "1.5rem" }}
                >
                  {user.fname?.charAt(0)?.toUpperCase() || "U"}
                </Typography>
              )}
            </Avatar>
              );
            })()}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  fontSize: { xs: "0.6rem", sm: "0.75rem" },
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  fontWeight: 600,
                }}
              >
                {user.user_type === "guest"
                  ? "Guest User"
                  : user.auth_provider === "google"
                    ? "Google Account"
                    : "Registered User"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "#fff",
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  mt: { xs: 0.25, sm: 0.5 },
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Name:{" "}
                {user.user_type === "guest"
                  ? getGuestDisplayName(user)
                  : `${user.fname || ""} ${user.lname || ""}`.trim() || "User"}
              </Typography>
              {user.user_type === "guest" && getGuestId(user) && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255, 255, 255, 0.6)",
                    fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    display: "block",
                    mt: 0.35,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Id: {getGuestId(user)}
                </Typography>
              )}
              {user.user_type !== "guest" && user.email && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255, 255, 255, 0.6)",
                    fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    display: "block",
                    mt: 0.35,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Email: {user.email}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: 2, py: 1 }}>
          {user.user_type === "guest" && (
            <>
              <MenuItem
                onClick={() => handleAuthModalOpen("register")}
                sx={{
                  "&:hover": {
                    bgcolor: "rgba(255, 0, 255, 0.2)",
                    transform: "translateX(6px)",
                  },
                  borderRadius: "12px",
                  py: { xs: 1, sm: 1.5 },
                  px: { xs: 1.5, sm: 2 },
                  my: { xs: 0.25, sm: 0.5 },
                  transition: "all 0.3s ease",
                }}
              >
                <Box
                  sx={{
                    width: { xs: 36, sm: 44 },
                    height: { xs: 36, sm: 44 },
                    borderRadius: "12px",
                    background:
                      "linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: { xs: 1.5, sm: 2 },
                    boxShadow: "0 4px 16px rgba(255,0,255,0.4)",
                  }}
                >
                  <PersonAddIcon sx={{ color: "#fff", fontSize: 22 }} />
                </Box>
                <Typography sx={{ color: "#fff", fontWeight: 600 }}>
                  Create Account
                </Typography>
              </MenuItem>
              <Divider sx={{ bgcolor: "rgba(255, 0, 255, 0.2)", my: 1 }} />
            </>
          )}

          <MenuItem
            onClick={handleLogout}
            sx={{
              "&:hover": {
                bgcolor: "rgba(255, 0, 100, 0.2)",
                transform: "translateX(6px)",
              },
              borderRadius: "12px",
              py: 1.5,
              px: 2,
              my: 0.5,
              transition: "all 0.3s ease",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #ff0066 0%, #ff00ff 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
                boxShadow: "0 4px 16px rgba(255,0,100,0.4)",
              }}
            >
              <LogoutIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            <Typography sx={{ color: "#fff", fontWeight: 600 }}>
              Sign Out
            </Typography>
          </MenuItem>
        </Box>
      </Menu>

      <AuthModal
        open={authModalOpen}
        onClose={handleAuthModalClose}
        onSuccess={handleAuthSuccess}
        initialMode={authMode}
      />
    </>
  );
};

export default UserAuthButton;
